// frontend/src/components/common/SubscriptionGuard.js
import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { Crown, Lock, AlertTriangle } from 'lucide-react';
import Button from './Button';

// Subscription Guard Component
export const SubscriptionGuard = ({ 
  children, 
  requiredFeature, 
  requiredTier, 
  fallback = null,
  showUpgradePrompt = true,
  className = ""
}) => {
  const { 
    hasFeatureAccess, 
    hasTierAccess, 
    subscription, 
    subscriptionLoading,
    getTierDisplayName,
    getUpgradeRecommendations
  } = useSubscription();

  // Show loading state
  if (subscriptionLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-4 bg-surface-sunken rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-surface-sunken rounded w-1/2"></div>
      </div>
    );
  }

  // Check access based on feature or tier
  let hasAccess = false;
  let accessType = '';

  if (requiredFeature) {
    hasAccess = hasFeatureAccess(requiredFeature);
    accessType = 'feature';
  } else if (requiredTier) {
    hasAccess = hasTierAccess(requiredTier);
    accessType = 'tier';
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If fallback is provided, render it
  if (fallback) {
    return <>{fallback}</>;
  }

  // If upgrade prompt is disabled, render nothing
  if (!showUpgradePrompt) {
    return null;
  }

  // Render upgrade prompt
  return (
    <div className={`rounded-card border border-border-default bg-surface-card p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-control bg-surface-sunken flex items-center justify-center">
          <Lock className="w-5 h-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" />
        </div>

        <div className="flex-1">
          <h3 className="text-section-title font-display text-ink mb-1">
            {accessType === 'feature' ? 'Premium Feature' : 'Upgrade Required'}
          </h3>

          <p className="text-body text-ink-muted mb-4">
            {accessType === 'feature'
              ? `This feature requires a higher subscription tier.`
              : `This feature requires ${getTierDisplayName(requiredTier)} or higher.`
            }
            {subscription && (
              <span className="block mt-1">
                Current plan: <span className="font-medium text-ink-soft">{getTierDisplayName()}</span>
              </span>
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button icon={Crown} onClick={() => window.location.href = '/billing'}>
              Upgrade Plan
            </Button>
            {/* Was '/subscriptions/plans', which is not a route: the catch-all
                silently dropped the user on the dashboard. */}
            <Button variant="secondary" onClick={() => window.location.href = '/subscription-selection'}>
              View Plans
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature-specific guards
export const VehicleProfilesGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredFeature="vehicle_profiles" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

export const JobManagementGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredFeature="job_management" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

export const AIDiagnosticsGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredFeature="ai_diagnostics" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

export const AdminGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredFeature="admin_dashboard" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

export const ProTierGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredTier="car_owner_pro" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

export const MechanicTierGuard = ({ children, fallback }) => (
  <SubscriptionGuard requiredTier="mechanic_essential" fallback={fallback}>
    {children}
  </SubscriptionGuard>
);

// Usage limit warning component
export const UsageLimitWarning = ({ currentUsage, limit, feature = "searches" }) => {
  const { subscription } = useSubscription();
  
  if (!subscription || subscription.search_limit === -1) return null;
  
  const usagePercentage = (currentUsage / subscription.search_limit) * 100;
  const isWarning = usagePercentage >= 80;
  const isCritical = usagePercentage >= 95;
  
  if (!isWarning) return null;
  
  return (
    <div className={`rounded-card p-4 mb-4 ${
      isCritical
        ? 'bg-danger-tint border border-danger-border'
        : 'bg-warning-tint border border-warning-border'
    }`}>
      <div className="flex items-start space-x-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 ${
          isCritical ? 'text-danger' : 'text-warning'
        }`} />

        <div className="flex-1">
          <h4 className={`text-body-strong ${
            isCritical ? 'text-danger' : 'text-warning'
          }`}>
            {isCritical ? 'Usage Limit Reached' : 'Usage Limit Warning'}
          </h4>

          <p className={`text-body mt-1 ${
            isCritical ? 'text-danger' : 'text-warning'
          }`}>
            You've used <span className="font-mono tabular-nums">{currentUsage}</span> of <span className="font-mono tabular-nums">{subscription.search_limit}</span> {feature} this month.
            {isCritical ? ' Consider upgrading for unlimited access.' : ' Consider upgrading to avoid interruptions.'}
          </p>
          
          <div className="mt-3">
            {/* Upgrading is the one primary action here; semantic colours stay
                on the tinted callout, never on a filled button. */}
            <Button size="sm" onClick={() => window.location.href = '/billing'}>
              Upgrade Now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
